import { addYears } from 'date-fns';
import { FC, lazy } from 'react';
import { useParams } from 'react-router-dom';
import { AuctionsParams } from 'src/apis/monolith/types';
import { useNow } from 'src/hooks/use-now';
import {
  TradeProposalsHookParams,
  useTradeProposals,
} from 'src/pages/reporting/secondary-tradings/overview/trade-proposals/use-trade-proposals';
import { useAuctions } from 'src/pages/transport/network-points/details/point-details/use-auctions';
import { useNwpDetailParams } from 'src/pages/transport/network-points/details/point-details/use-page-params';
import { NetworkPointRequest } from 'src/pages/transport/network-points/details/use-network-point';
import { useNwpCapacityGraph } from 'src/pages/transport/network-points/details/use-nwp-capacity-graph';
import { useMemoOne } from 'use-memo-one';

const Page = lazy(
  () => import('src/pages/transport/network-points/details/point-details/page')
);

type Props = {
  networkPoint: NetworkPointRequest;
};

export const NetworkPointsDetails: FC<Props> = ({ networkPoint }) => {
  const pageParams = useNwpDetailParams();
  const { oldNetworkPointId } = useParams<{ oldNetworkPointId: string }>();

  const nwpCapacityGraph = useNwpCapacityGraph({ oldNetworkPointId });
  const now = useNow();

  const longTermParams = useMemoOne<AuctionsParams>(
    () => ({
      periodType: ['MONTH', 'QUARTER', 'YEAR', 'REST_OF_YEAR'],
      phase: ['PUBLISHED', 'OPEN', 'CLOSED'],
      networkPoint: [oldNetworkPointId],
      pageSize: pageParams.longTermAuctions.value.pageSize,
      start: pageParams.longTermAuctions.value.start,
      runtimeFrom: new Date(now).toISOString(),
      runtimeTo: addYears(now, 15).toISOString(),
    }),
    [
      oldNetworkPointId,
      pageParams.longTermAuctions.value.pageSize,
      pageParams.longTermAuctions.value.start,
    ]
  );
  const longTermAuctions = useAuctions({ params: longTermParams });

  const shortTermParams = useMemoOne<AuctionsParams>(
    () => ({
      periodType: ['DAY', 'WITHINDAY'],
      phase: ['OPEN'],
      networkPoint: [oldNetworkPointId],
      pageSize: pageParams.shortTermAuctions.value.pageSize,
      start: pageParams.shortTermAuctions.value.start,
      runtimeFrom: new Date(now).toISOString(),
      runtimeTo: addYears(now, 15).toISOString(),
    }),
    [
      oldNetworkPointId,
      pageParams.shortTermAuctions.value.pageSize,
      pageParams.shortTermAuctions.value.start,
    ]
  );
  const shortTermAuctions = useAuctions({ params: shortTermParams });

  const offeredProposalsParams = useMemoOne<TradeProposalsHookParams>(
    () => ({
      proposalType: 'PROPOSAL_TO_SELL',
      networkPoint: oldNetworkPointId,
      pageSize: pageParams.offeredProposals.value.pageSize,
      start: pageParams.offeredProposals.value.start,
    }),
    [
      oldNetworkPointId,
      pageParams.offeredProposals.value.pageSize,
      pageParams.offeredProposals.value.start,
    ]
  );
  const offeredProposals = useTradeProposals({
    params: offeredProposalsParams,
  });

  const requestedProposalsParams = useMemoOne<TradeProposalsHookParams>(
    () => ({
      proposalType: 'PROPOSAL_TO_BUY',
      networkPoint: oldNetworkPointId,
      pageSize: pageParams.requestedProposals.value.pageSize,
      start: pageParams.requestedProposals.value.start,
    }),
    [
      oldNetworkPointId,
      pageParams.requestedProposals.value.pageSize,
      pageParams.requestedProposals.value.start,
    ]
  );
  const requestedProposals = useTradeProposals({
    params: requestedProposalsParams,
  });

  return (
    <Page
      pageParams={pageParams}
      networkPoint={networkPoint}
      nwpCapacityGraph={nwpCapacityGraph}
      longTermAuctions={longTermAuctions}
      shortTermAuctions={shortTermAuctions}
      offeredProposals={offeredProposals}
      requestedProposals={requestedProposals}
    />
  );
};
